import React, { useState } from 'react'
import { REPLY_ATTACHMENT_TYPES } from '../shared/OutboundPhotoPicker.jsx'

/**
 * Phone composer — expanded state for the reply UI on mobile-v2.
 *
 * Layout (top to bottom):
 *   - Horizontally-scrolling AI chip row (Suggest / Rewrite + context)
 *   - Optional channel warning banner (eBay / Reverb)
 *   - Textarea (16px font to prevent iOS zoom-on-focus)
 *   - Send row: attach, "Mark as ▾" status select, Cancel, Send
 *
 * The status select defaults to "Keep status" so a plain Send never
 * changes ticket state by accident. Pick "Resolved" / "Awaiting" / etc.
 * BEFORE tapping Send to apply that status as part of the same action.
 */
function PhoneComposer({
  replyText, setReplyText,
  sending,
  replyFiles,
  aiLoading, rewriteLoading,
  rewriteContext, setRewriteContext,
  suggestTemplate, setSuggestTemplate,
  onAiSuggest, onAiRewrite,
  channelWarning,
  onSend, onCancel
}) {
  const [statusChange, setStatusChange] = useState('keep')

  const handleSend = () => {
    onSend(statusChange === 'keep' ? null : statusChange)
  }

  return (
    <div className="comp">
      <div className="comp-aichips">
        <button
          className="comp-chip comp-chip--suggest"
          onClick={onAiSuggest}
          disabled={aiLoading}
        >
          {aiLoading ? '🤖 Thinking…' : '🤖 Suggest reply'}
        </button>
        <select
          className="comp-chip-select"
          value={suggestTemplate}
          onChange={e => setSuggestTemplate(e.target.value)}
          aria-label="AI Suggest template"
        >
          <option value="auto">Auto</option>
          <option value="market_price">Market price</option>
          <option value="counter_offer">Counter offer</option>
          <option value="fair_price">Ask fair price</option>
        </select>
        <button
          className="comp-chip comp-chip--rewrite"
          onClick={() => onAiRewrite(rewriteContext)}
          disabled={rewriteLoading || !replyText.trim()}
        >
          {rewriteLoading ? '✨ Rewriting…' : '✨ Rewrite'}
        </button>
        <select
          className="comp-chip-select"
          value={rewriteContext}
          onChange={e => setRewriteContext(e.target.value)}
          aria-label="Rewrite context"
        >
          <option value="auto">Auto</option>
          <option value="trade_in">Trade-In</option>
          <option value="order">Order</option>
        </select>
      </div>
      {channelWarning && <div className="comp-warn">{channelWarning}</div>}
      <textarea
        className="comp-textarea"
        placeholder="Type your reply…"
        value={replyText}
        onChange={e => setReplyText(e.target.value)}
        autoFocus
      />
      {/* Several files per reply (Trac #1407). replyFiles is useOutboundPhotos. */}
      {replyFiles?.error && <div className="comp-warn" role="alert">{replyFiles.error}</div>}
      {replyFiles?.files.map((file, index) => (
        <div className="comp-filechip" key={`${file.name}-${index}`}>
          <span>📎 {file.name}</span>
          <button
            type="button"
            onClick={() => replyFiles.removeAt(index)}
            aria-label={`Remove ${file.name}`}
          >×</button>
        </div>
      ))}
      <div className="comp-sendrow">
        {/* File input is hidden; the <label> is the visible touch target.
            Using a label keeps the native iOS file picker (Photo Library /
            Take Photo / Browse) — a <button>+ref.click() also works but
            sometimes loses the user gesture chain on iOS Safari. */}
        <label className="icon-btn" aria-label="Attach file" style={{ cursor: 'pointer' }}>
          📎
          <input
            type="file"
            multiple
            accept={REPLY_ATTACHMENT_TYPES}
            disabled={replyFiles?.preparing}
            style={{ display: 'none' }}
            onChange={e => replyFiles?.addFromInput(e)}
          />
        </label>
        <select
          className="comp-status"
          value={statusChange}
          onChange={e => setStatusChange(e.target.value)}
          aria-label="Change status when sending"
        >
          <option value="keep">Mark as: Keep status</option>
          <option value="resolved">Mark as: Resolved</option>
          <option value="awaiting_reply">Mark as: Awaiting reply</option>
          <option value="waiting_customer">Mark as: Waiting on customer</option>
          <option value="waiting_third_party">Mark as: 3rd party</option>
        </select>
        <button className="comp-cancel" onClick={onCancel}>Cancel</button>
        <button
          className="comp-send"
          onClick={handleSend}
          disabled={!replyText.trim() || sending || replyFiles?.preparing}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  )
}

export default PhoneComposer
